import { useAuth } from "@/hooks/useAuth";
import { exportProgress } from "@/lib/progressExport";
import { cancelFirstSync, resolveFirstSync } from "@/lib/sync";

/**
 * Asked once, before the first sync into an account that already has progress
 * while this device has some too. Mounted globally: sign-in returns to `/`,
 * so the question has to be answerable from any page. Nothing is written until
 * a button is pressed; "Not now" also drops the session.
 */
export function FirstSyncDialog() {
  const { status, pending } = useAuth();
  if (status !== "needs-decision" || !pending) return null;

  return (
    <div className="dialog-backdrop" role="presentation">
      <div className="dialog-panel" role="dialog" aria-modal="true" aria-labelledby="first-sync-title">
        <div className="t-eyebrow dialog-eyebrow">Before the first sync</div>
        <h2 id="first-sync-title" className="t-display dialog-title">
          This account already has progress
        </h2>
        <p className="dialog-body">
          This device has progress too. Merge keeps the newer answer for every word and adds your
          study days together; nothing is dropped. Replace wipes this device and takes the account as
          it is.
        </p>
        <dl className="dialog-facts">
          <div>
            <dt>On this device</dt>
            <dd className="t-mono t-num">{pending.localWords} words</dd>
          </div>
          <div>
            <dt>In the account</dt>
            <dd className="t-mono t-num">{pending.remoteWords} words</dd>
          </div>
          <div>
            <dt>In both</dt>
            <dd className="t-mono t-num">
              {pending.both} · {pending.newerHere} newer here · {pending.newerThere} newer there
            </dd>
          </div>
        </dl>
        <div className="dialog-actions">
          <button className="btn btn-solid btn-lg" onClick={() => resolveFirstSync("merge")} type="button">
            Merge, keep everything
          </button>
          <button className="btn dialog-danger" onClick={() => resolveFirstSync("use-account")} type="button">
            Replace this device with the account
          </button>
          <button className="btn btn-ghost" onClick={() => void cancelFirstSync()} type="button">
            Not now
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportProgress()} type="button">
            Export a backup first
          </button>
        </div>
      </div>
    </div>
  );
}
